import React, { FC } from 'react';
import classNames from 'classnames';

import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { ModifierTreeItem } from 'Src/feature/modifier/useModifierTree';
import { ModifierCommandPaletteChip } from 'Src/feature/modifier-command/ModifierCommandPaletteChip';
import { ModifierCommandUI } from 'Src/feature/modifier-command/ModifierCommandUI';
import { ModelType } from 'Src/models/modelPath';

import styles from './ModifierCardButton.module.scss';

interface ModifierCardButtonProps {
  item: ModifierTreeItem;
  onClick: (item: ModifierTreeItem) => void;
  onIncrement: (item: ModifierTreeItem) => void;
  onDecrement: (item: ModifierTreeItem) => void;
  command?: ModifierCommandUI;
  commandSelected?: boolean;
  priceDiff: number | undefined;
  allowsDuplicates: boolean;
  disabled: boolean;
}

const ModifierCardButton: FC<ModifierCardButtonProps> = ({
  item,
  onClick,
  onIncrement,
  onDecrement,
  priceDiff,
  command,
  commandSelected,
  allowsDuplicates,
  disabled,
  ...rest
}) => {

  // extra sauce for nested modifier validation
  // - we want to omit an invalid icon when a nested group is invalid but the parent is valid
  const isValid = item.valid === false && item.validation && !item.validation.required
    ? true
    : item.valid;
  
  const showBtnQuantity = item.isSelected && allowsDuplicates && item.type === ModelType.modifier;
  // show checkmark if no duplicates allowed, otherwise show quantity marker
  const hideIconCheck = showBtnQuantity || !item.isSelected;
  const hideIconError = isValid !== false;
  const hideIconNext = !item.children.length;
  const hidePriceDiff = !priceDiff;
  const showQuantityCount = showBtnQuantity && item.selections.quantity > 0;
  const showCommand = command && command.intents.includes('badge');

  return (
    <div
      className={classNames(styles.root, {
        [styles.is_selected]: item.isSelected,
        [styles.is_disabled]: disabled,
        [styles.is_valid]: isValid === true,
        [styles.is_invalid]: isValid === false,
        [styles.show_btn_quantity]: showBtnQuantity,
        [styles.hide_icon_check]: hideIconCheck,
        [styles.hide_icon_error]: hideIconError,
        [styles.hide_icon_next]: hideIconNext,
        [styles.hide_price_diff]: hidePriceDiff,
        [styles.hide_quantity_count]: !showQuantityCount,
        [styles.hide_command]: !showCommand,
      })}
    >
      <button
        {...rest}
        className={styles.btn_main}
        onClick={() => {
          if (allowsDuplicates && item.selections.quantity > 1 && !commandSelected) onDecrement(item);
          else onClick(item);
        }}
        disabled={disabled}
      >
        {/* main copy should fill available space and when copy extends past available space; ellipsis */}
        <Copy className={styles.name} ellipsis variant="body" weight="regular" value={item.name}/>
        {/* icons should flow from left to right of above element(s) or be hidden w/ display: none */}
        <FontIcon className={styles.icon_check} name="check" size="sm"/>
        <FontIcon className={styles.icon_error} name="warning" size="sm" color="error"/>
        <FontIcon className={styles.icon_next} name="navigate_next" size="sm"/>
        {/* "priceDiff" should flow from left to right of above element(s) or be hidden w/ display: none */}
        <Copy className={styles.price_diff} type="currency" value={priceDiff} />
        {/* "count" should flow from left to right of above element(s) or be hidden w/ display: none */}
        <Copy className={styles.quantity_count} type="xCount" value={item.selections.quantity} />
        {/* "command chip" will not flow as it is an absolute positioned element and/or be hidden w/ display: none */}
        <ModifierCommandPaletteChip
          className={styles.command}
          label={command?.model.name}
          kind={command?.model.kind}
        />
      </button>
      {/* "plus icon button" should flow from left to right of above element(s)  */}
      <div className={styles.btn_quantity_wrapper}>
        <button className={styles.btn_quantity} onClick={() => onIncrement(item)} disabled={disabled}><FontIcon name="add"/></button>
      </div>
    </div>
  );
};

export { ModifierCardButton };
export type { ModifierCardButtonProps };
